// src/AuthActionHandler.js
//
// E-posta doğrulama action handler — /auth/action rotası.
//
// Araştırma dayanağı (Instagram/WhatsApp/Google incelemesi, 2026-06-10):
// Google doğrulamayı kayıt sihirbazının İÇİNDE tamamlatır (inline);
// Instagram linke tıklayanı kendi markalı sayfasına indirir. Firebase'in
// varsayılanı ise jenerik, markasız "__/auth/action" sayfasıdır — kullanıcı
// MyLASA'dan çıkıp Google'ın soğuk sayfasına düşer. Bu bileşen, doğrulama
// linkini KENDİ markalı sayfamızda karşılayıp kullanıcıyı uygulamaya geri
// akıtır (Google'ın "inline devam" hissine en yakın çözüm).
//
// Devreye alma: Identity Toolkit config'inde callbackUri
// https://mylasa-final.web.app/auth/action yapılana kadar (bkz.
// functions/src/admin/updateAuthEmailTemplates.ts) maillerdeki linkler
// Firebase'in varsayılan sayfasına gitmeye devam eder — bu sayfa hazır
// bekler, hiçbir akışı bozmaz.
//
// Davranış:
//   - mode=verifyEmail → applyActionCode(oobCode) → markalı başarı ekranı
//     → 2.5 sn sonra (veya butonla) uygulamaya dönüş. Oturum açıksa
//     EmailVerifyGate'in poll'u zaten doğrulamayı yakalar.
//   - Diğer mode'lar (resetPassword, recoverEmail...) → Firebase'in hosted
//     handler'ına (__/auth/action) aynı query ile yönlendirilir; şifre
//     sıfırlama akışı bozulmaz. (Bu görev yalnız e-posta doğrulama kapsar.)
//   - identitytoolkit App Check ENFORCED → applyActionCode öncesi token
//     beklenir (EmailVerifyGate'teki 400 Bad Request düzeltmesiyle aynı).

import { useEffect, useRef, useState } from 'react';
import { auth, awaitAppCheckToken } from './firebase';
import { applyActionCode } from 'firebase/auth';
import { hataAlani, hataMesaji } from './utils/hataAlani';
import { IkonKoprusu } from "./icon-system/runtime/IkonKoprusu";

const APP_HOME = '/';
const FIREBASE_HOSTED_HANDLER = '/__/auth/action';
const AUTO_REDIRECT_MS = 2500;

export default function AuthActionHandler() {
  // state: 'working' | 'success' | 'expired' | 'error'
  const [state, setState] = useState('working');
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return; // StrictMode double-mount koruması — oobCode tek kullanımlık
    ranRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    const oobCode = params.get('oobCode');

    // E-posta doğrulama dışındaki action'lar Firebase'in hazır sayfasında
    // işlenmeye devam eder (şifre sıfırlama vb. bu görevin kapsamı dışı).
    if (mode !== 'verifyEmail') {
      window.location.replace(FIREBASE_HOSTED_HANDLER + window.location.search);
      return;
    }
    if (!oobCode) {
      setState('error');
      return;
    }

    (async () => {
      try {
        await awaitAppCheckToken();
        await applyActionCode(auth, oobCode);
        // Oturum bu tarayıcıda açıksa token'daki email_verified'ı tazele;
        // başka cihazda açıksa oradaki EmailVerifyGate poll'u yakalar.
        try {
          await auth.currentUser?.reload();
          // Token claim tazelemesi (email_verified) — sunucu enforcement'ı için şart
          await auth.currentUser?.getIdToken(true);
        } catch {}
        setState('success');
        setTimeout(() => { window.location.replace(APP_HOME); }, AUTO_REDIRECT_MS);
      } catch (err) {
        const kod = hataAlani(err, 'code');
        if (kod === 'auth/invalid-action-code' || kod === 'auth/expired-action-code') {
          // DAYANIKLILIK: kod "kullanılmış" görünüyorsa gerçekten doğrulanmış
          // olabilir (hosted sayfa önce uyguladı / çift tıklama / mail
          // tarayıcısı linki önceden açtı). Oturum bu tarayıcıda açıksa
          // kullanıcıyı tazele — doğrulanmışsa HATA değil BAŞARI göster.
          try {
            await auth.currentUser?.reload();
            if (auth.currentUser?.emailVerified) {
              setState('success');
              setTimeout(() => { window.location.replace(APP_HOME); }, AUTO_REDIRECT_MS);
              return;
            }
          } catch { /* oturum yok/tazelenemedi — expired akışına düş */ }
          // Link kullanılmış ya da süresi dolmuş — yeni kod istemesi için
          // uygulamaya yönlendirilir (EmailVerifyGate "Tekrar gönder" sunar).
          setState('expired');
        } else {
          // eslint-disable-next-line no-console
          console.error('[AuthActionHandler] applyActionCode error:', kod, hataMesaji(err));
          setState('error');
        }
      }
    })();
  }, []);

  const goApp = () => { window.location.replace(APP_HOME); };

  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: 24,
      textAlign: 'center', gap: 12, background: '#fafafa',
    }}>
      {state === 'working' && (
        <>
          <div style={{ fontSize: 40 }}><IkonKoprusu aile="system.pending" eski="⏳" /></div>
          <h2 style={{ margin: 0 }}>E-postan doğrulanıyor…</h2>
          <p style={{ maxWidth: 360, color: '#555', margin: 0 }}>Bu birkaç saniye sürer.</p>
        </>
      )}
      {state === 'success' && (
        <>
          <div style={{ fontSize: 40 }}>✅</div>
          <h2 style={{ margin: 0 }}>E-postan doğrulandı!</h2>
          <p style={{ maxWidth: 360, color: '#555', margin: 0 }}>
            Hesabın hazır. MyLASA'ya yönlendiriliyorsun…
          </p>
          <button
            className="pressable"
            type="button"
            onClick={goApp}
            style={{
              padding: '10px 20px', borderRadius: 8, border: 'none',
              background: '#0095f6', color: '#fff', fontWeight: 600, cursor: 'pointer',
            }}
          >
            MyLASA'ya devam et
          </button>
        </>
      )}
      {state === 'expired' && (
        <>
          <div style={{ fontSize: 40 }}>⌛</div>
          <h2 style={{ margin: 0 }}>Bağlantının süresi dolmuş</h2>
          <p style={{ maxWidth: 360, color: '#555', margin: 0 }}>
            Bu doğrulama bağlantısı kullanılmış ya da süresi geçmiş.
            Uygulamaya dönüp &quot;Tekrar gönder&quot; ile yeni bir bağlantı isteyebilirsin.
          </p>
          <button
            className="pressable"
            type="button"
            onClick={goApp}
            style={{
              padding: '10px 20px', borderRadius: 8, border: 'none',
              background: '#0095f6', color: '#fff', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Uygulamaya dön
          </button>
        </>
      )}
      {state === 'error' && (
        <>
          <div style={{ fontSize: 40 }}><IkonKoprusu aile="system.warning" eski="⚠️" /></div>
          <h2 style={{ margin: 0 }}>Bir şeyler ters gitti</h2>
          <p style={{ maxWidth: 360, color: '#555', margin: 0 }}>
            Doğrulama tamamlanamadı. İnternet bağlantını kontrol edip
            e-postadaki bağlantıya yeniden tıkla; olmazsa uygulamadan yeni
            bağlantı iste.
          </p>
          <button
            className="pressable"
            type="button"
            onClick={goApp}
            style={{
              padding: '10px 20px', borderRadius: 8, border: 'none',
              background: '#0095f6', color: '#fff', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Uygulamaya dön
          </button>
        </>
      )}
    </div>
  );
}
